// src/pages/Overview/Overview.tsx

import {
  ExpandLessTwoTone,
  ExpandMoreTwoTone,
  HighlightOffTwoTone,
} from '@mui/icons-material'
import {
  Box,
  Collapse,
  Container,
  FormControl,
  IconButton,
  ImageList,
  ImageListItem,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { FolderPool } from '@/types/domain/folder'

import { ASC, DESC, getKindOfUseIcon, getStorage, setStorage } from '../../api'
import {
  CardFolderStatus,
  DashboardAlerts,
  HStack,
  Preloader,
  usePreloader,
} from '../../components/'
import { useAppConfig, useFolderPool } from '../../hooks'
import useRenderCount from '../../hooks/tools/useRenderCount'

import { Maintenance, OperatorGreeting } from './components'

type Props = {
  gap?: number
  PAGE_CONFIG?: string
}

export const Overview = ({
  gap = 10,
  PAGE_CONFIG = 'settings_dashboard',
}: Props) => {
  // render performance
  const rc = useRenderCount()
  console.log('Overview', rc)

  const { isLoading, percentage } = usePreloader()

  const theme = useTheme()
  const isXS = useMediaQuery(theme.breakpoints.only('xs'))
  const isSM = useMediaQuery(theme.breakpoints.only('sm'))
  const isMD = useMediaQuery(theme.breakpoints.only('md'))
  const isLG = useMediaQuery(theme.breakpoints.only('lg'))
  const isXL = useMediaQuery(theme.breakpoints.only('xl'))

  const determineCols = () =>
    isXL ? 3 : isLG ? 2 : isMD ? 2 : isSM ? 1 : isXS ? 1 : 0

  const { appConfig } = useAppConfig()
  const isWideScreen: boolean = appConfig?.wideScreen || true

  const fPool: FolderPool = useFolderPool()

  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))

  const [order, setOrder] = useState<string>(ASC)
  const [usage, setUsage] = useState<string>('*')

  const kindOfUses = [
    ...new Set(
      fPool
        .map(f => f.attributes.kind_of_use)
        .map(f => f?.split(/[&+,;]/).map(u => u.trim()))
        .flat()
        .sort()
    ),
  ]

  const inputFilter = f =>
    f.attributes.name.toLowerCase().includes(config.objectFilter.toLowerCase())

  const sortFilter = (a, b) => {
    if (a.attributes.name < b.attributes.name) return order === DESC ? 1 : -1

    return order === DESC ? -1 : 1
  }

  const onChangeFilter = event => {
    const objectFilter = event.target.value

    setConfig(prev => ({ ...prev, objectFilter }))
  }

  const onClearFilter = () => {
    const objectFilter = ''

    setConfig(prev => ({ ...prev, objectFilter }))
  }

  const onChangeOrder = () => {
    setOrder(prev => (prev === ASC ? DESC : ASC))
  }

  useEffect(() => setStorage(PAGE_CONFIG, config), [config, PAGE_CONFIG])

  return (
    <Collapse in={!isLoading}>
      <Preloader
        caption={`Übersicht laden ... ${percentage}%`}
        isLoading={isLoading}
        percentage={percentage}
      />

      <Container maxWidth={isWideScreen === true ? false : 'xl'}>
        <OperatorGreeting />
        <Maintenance />

        <Collapse in={appConfig.alertsEnabled && fPool.length > 0}>
          <DashboardAlerts sx={{ my: 5 }} />
        </Collapse>

        {fPool.length > 0 && (
          <HStack
            sx={{ my: 2, mx: 0, width: 1 }}
            justifyContent={'flex-end'}
            alignItems={'stretch'}
          >
            <TextField
              InputProps={{
                endAdornment: (
                  <InputAdornment position={'end'}>
                    {config.objectFilter && (
                      <Tooltip title={'Filter löschen'}>
                        <IconButton onClick={onClearFilter}>
                          <HighlightOffTwoTone />
                        </IconButton>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              }}
              label={'Gebäude finden'}
              onChange={onChangeFilter}
              sx={{ my: 0, mr: 1 }}
              value={config.objectFilter}
            />

            <Tooltip title={order === DESC ? 'Z-A' : 'A-Z'}>
              <ToggleButtonGroup>
                <ToggleButton onClick={onChangeOrder} value={order}>
                  {order === ASC && <ExpandLessTwoTone />}
                  {order === DESC && <ExpandMoreTwoTone />}
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>

            <FormControl>
              <InputLabel>Nutzungsart</InputLabel>

              <Select
                label={'Nutzungsart'}
                onChange={event => setUsage(event.target.value)}
                renderValue={selected => (
                  <Typography>
                    {selected === '*' ? 'Alle' : selected}
                  </Typography>
                )}
                sx={{ width: 220, m: 0 }}
                value={usage}
              >
                <MenuItem value={'*'}>Alle</MenuItem>

                {kindOfUses
                  .filter(Boolean)
                  .sort()
                  .map(item => (
                    <MenuItem key={item} value={item}>
                      <Box sx={{ mr: 1 }}>{getKindOfUseIcon(item)}</Box>
                      {item}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </HStack>
        )}

        {fPool.length > 0 && (
          <ImageList cols={determineCols()} gap={gap} sx={{ width: 1 }}>
            {fPool
              ?.filter(f =>
                usage === '*' ? f : f.attributes.kind_of_use?.includes(usage)
              )
              ?.filter(inputFilter)
              .sort(sortFilter)
              .map(f => (
                <ImageListItem key={f.id}>
                  <CardFolderStatus id={Number(f.id)} />
                </ImageListItem>
              ))}
          </ImageList>
        )}
      </Container>
    </Collapse>
  )
}
